import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useAdminApplication, useAdminSession, useAdminUiStore } from "../providers";
import { safeAdminReturnPath } from "../routes";

export interface AdminReturnContext {
  readonly sessionEpoch: number;
  readonly originEntryKey: string;
  readonly originPath: string;
  readonly contextKey: string;
  readonly originLabel: string;
}

interface AdminNavigationState {
  readonly adminReturnContext?: AdminReturnContext;
}

export const ADMIN_LIST_TARGET_EXCLUDED_NOTICE = "처리한 항목이 현재 검색 조건에서 제외되었습니다.";

function listEntryContextKey(contextKey: string, entryKey: string): string {
  return `list-entry:${contextKey}:${entryKey}`;
}

function readReturnContext(value: unknown): AdminReturnContext | undefined {
  if (!value || typeof value !== "object") return undefined;
  const candidate = (value as AdminNavigationState).adminReturnContext;
  if (!candidate || typeof candidate !== "object") return undefined;
  if (!Number.isSafeInteger(candidate.sessionEpoch)) return undefined;
  if (!candidate.originEntryKey || !candidate.contextKey || !candidate.originLabel) return undefined;
  if (!safeAdminReturnPath(candidate.originPath)) return undefined;
  return candidate;
}

function directionalParticle(label: string): "로" | "으로" {
  const last = label.codePointAt(label.length - 1);
  if (last === undefined || last < 0xac00 || last > 0xd7a3) return "로";
  const finalConsonant = (last - 0xac00) % 28;
  return finalConsonant !== 0 && finalConsonant !== 8 ? "으로" : "로";
}

export function useAdminNavigation(options: {
  readonly contextKey: string;
  readonly defaultListPath: string;
  readonly originLabel: string;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const application = useAdminApplication();
  const session = useAdminSession();
  const remembered = useAdminUiStore((state) => state.listContexts[options.contextKey]);
  const previousEntryKey = useAdminUiStore((state) => (
    state.navigationIndex > 0 ? state.navigationEntries[state.navigationIndex - 1] : undefined
  ));
  const rememberListContext = useAdminUiStore((state) => state.rememberListContext);
  const returnContext = readReturnContext(location.state);

  const openDetail = useCallback((to: string, triggerKey?: string) => {
    const table = document.activeElement?.closest(".cp-data-table") as HTMLElement | null;
    const originPath = `${location.pathname}${location.search}`;
    const currentRemembered = application.uiStore.getState().listContexts[options.contextKey] ?? remembered;
    const context = {
      path: originPath,
      page: currentRemembered?.page ?? (Number(new URLSearchParams(location.search).get("page")) || 0),
      filters: currentRemembered?.filters ?? {},
      entryKey: location.key,
      scrollY: window.scrollY,
      tableScrollLeft: table?.scrollLeft ?? 0,
      triggerKey,
    };
    const entryContextKey = listEntryContextKey(options.contextKey, location.key);
    rememberListContext(options.contextKey, context);
    rememberListContext(entryContextKey, context);
    navigate(to, {
      state: {
        adminReturnContext: {
          sessionEpoch: session.sessionEpoch,
          originEntryKey: location.key,
          originPath,
          contextKey: entryContextKey,
          originLabel: options.originLabel,
        } satisfies AdminReturnContext,
      } satisfies AdminNavigationState,
    });
  }, [application.uiStore, location.key, location.pathname, location.search, navigate, options.contextKey, options.originLabel, remembered, rememberListContext, session.sessionEpoch]);

  const backToList = useCallback(() => {
    const sameSessionReturnPath = returnContext?.sessionEpoch === session.sessionEpoch
      ? safeAdminReturnPath(returnContext.originPath)
      : null;
    const hasActualPreviousEntry = Boolean(
      sameSessionReturnPath
      && returnContext?.originEntryKey === previousEntryKey,
    );
    if (hasActualPreviousEntry) {
      navigate(-1);
      return;
    }
    navigate(sameSessionReturnPath ?? remembered?.path ?? options.defaultListPath, { replace: true });
  }, [navigate, options.defaultListPath, previousEntryKey, remembered?.path, returnContext, session.sessionEpoch]);

  const openRelated = useCallback((to: string, originLabel: string) => {
    navigate(to, {
      state: {
        adminReturnContext: {
          sessionEpoch: session.sessionEpoch,
          originEntryKey: location.key,
          originPath: `${location.pathname}${location.search}`,
          contextKey: `entry:${location.key}`,
          originLabel,
        } satisfies AdminReturnContext,
      } satisfies AdminNavigationState,
    });
  }, [location.key, location.pathname, location.search, navigate, session.sessionEpoch]);

  const focusListResults = useCallback(() => {
    window.requestAnimationFrame(() => {
      const resultsHeading = Array.from(document.querySelectorAll<HTMLElement>("[data-admin-results-heading]"))
        .find((element) => !element.closest("[hidden]"));
      resultsHeading?.focus();
    });
  }, []);

  return {
    backLabel: returnContext?.sessionEpoch === session.sessionEpoch
      ? `${returnContext.originLabel}${directionalParticle(returnContext.originLabel)} 돌아가기`
      : "목록으로 돌아가기",
    backPath: safeAdminReturnPath(returnContext?.originPath) ?? remembered?.path ?? options.defaultListPath,
    backToList,
    focusListResults,
    openDetail,
    openRelated,
    returnContext,
  };
}

export function useRestoreAdminListViewport(contextKey: string, ready: boolean) {
  const location = useLocation();
  const entryContextKey = listEntryContextKey(contextKey, location.key);
  const entryContext = useAdminUiStore((state) => state.listContexts[entryContextKey]);
  const fallbackContext = useAdminUiStore((state) => state.listContexts[contextKey]);
  const currentPath = `${location.pathname}${location.search}`;
  const remembered = entryContext?.path === currentPath ? entryContext : fallbackContext;
  const restoredKey = useRef<string | undefined>(undefined);
  const [targetExcluded, setTargetExcluded] = useState(false);

  useEffect(() => {
    if (!ready || !remembered?.entryKey || restoredKey.current === remembered.entryKey) return;
    if (currentPath !== remembered.path) return;
    const entryKey = remembered.entryKey;
    const frame = window.requestAnimationFrame(() => {
      const targetScrollY = remembered.scrollY ?? 0;
      if (window.scrollY !== targetScrollY) window.scrollTo({ top: targetScrollY });
      const table = document.querySelector<HTMLElement>(".cp-data-table");
      if (table) table.scrollLeft = remembered.tableScrollLeft ?? 0;
      if (remembered.triggerKey) {
        const trigger = Array.from(document.querySelectorAll<HTMLElement>("[data-admin-trigger]"))
          .find((element) => element.dataset.adminTrigger === remembered.triggerKey);
        if (trigger) {
          trigger.focus({ preventScroll: true });
          setTargetExcluded(false);
        } else {
          const resultsHeading = Array.from(document.querySelectorAll<HTMLElement>("[data-admin-results-heading]"))
            .find((element) => !element.closest("[hidden]"));
          resultsHeading?.focus({ preventScroll: true });
          setTargetExcluded(true);
        }
      }
      restoredKey.current = entryKey;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [contextKey, currentPath, entryContextKey, ready, remembered]);

  return targetExcluded ? ADMIN_LIST_TARGET_EXCLUDED_NOTICE : "";
}
